/**
 * The PWA's outbound heartbeat — **P5's client half.**
 *
 * ⚠️ **A route with no caller is the defect P5 exists to close, not a step towards closing it.**
 * `/v1/deployments/heartbeat` has existed since F24 and could only ever be called by a BYOC engine,
 * which is why `last_heartbeat_at` and `engine_version` are still `null` for every real tenant. So
 * this file matters more than the route it calls: without it, P5 would be "built" in exactly the
 * sense this repository keeps catching.
 *
 * ⚠️ **Counts and a version string. Nothing else can be sent**, because nothing else is read — the
 * body is constructed here from four scalars and the server re-validates each one. There is no
 * pass-through of a record, a field name or a note.
 *
 * ⚠️ **Never throws, and never blocks the thing that triggered it.** Telemetry that can fail a
 * client's match run is worse than no telemetry: the run is the product, this is our fleet view of
 * it. Every failure path resolves quietly to `false`.
 *
 * ⚠️ **Local mode only, and only for a client with an org.** An Entry/Sandbox client HAS an org
 * ([[the tier is not the absence of identity]]), which is what makes this reportable at all; a
 * signed-out device has nothing to report against and must not try.
 */
import { ENGINE_VERSION } from '@dedupr/match-core';
import { accessToken } from '../auth.js';

export interface HeartbeatInput {
  /** Live records held on this device. `null` when the caller genuinely does not know. */
  recordsUnderManagement: number | null;
  runsCompleted: number | null;
  runsFailed: number | null;
}

/**
 * Reports once. Resolves `true` only when the control plane recorded it.
 *
 * `fetchImpl` is injectable so the test drives the real body-construction path rather than a
 * re-description of it — the shape that crosses the boundary is the thing worth asserting.
 */
export async function reportHeartbeat(
  input: HeartbeatInput,
  fetchImpl: typeof fetch = fetch
): Promise<boolean> {
  try {
    const token = await accessToken();
    /* No session, nothing to report against. Deliberately not an error: a signed-out device is a
       normal state for this app, not a fault. */
    if (!token) return false;

    const res = await fetchImpl('/api/telemetry/heartbeat', {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({
        engine_version: ENGINE_VERSION,
        records_under_management: input.recordsUnderManagement,
        runs_completed: input.runsCompleted,
        runs_failed: input.runsFailed,
      }),
    });
    return res.ok;
  } catch {
    /* Offline, blocked, or refused — all the same answer here. The run already succeeded. */
    return false;
  }
}
